import { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, Save, DollarSign, BarChart3, Calculator, Clock, Info, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { apiGet, apiPatch } from '@/lib/api';

// ── Types ──────────────────────────────────────────────────────

interface ModelPricing {
  input_per_mtok: number;
  output_per_mtok: number;
  cache_5m_multiplier: number;
  cache_1h_multiplier: number;
  cache_read_multiplier: number;
}

interface BillingConfig {
  margin_factor: number;
  usd_to_eur_rate: number;
  tokens_per_eur: number;
  web_search_cost: number;
  models: { opus: ModelPricing; sonnet: ModelPricing };
}

interface ConfigLogEntry {
  id: string;
  changedBy: string | null;
  changes: Record<string, { old: unknown; new: unknown }>;
  createdAt: string;
}

interface ConfigResponse {
  config: BillingConfig;
  changelog: ConfigLogEntry[];
}

interface AnalyticsOverview {
  totalCostUsd: number;
  totalHypemaTokens: number;
  totalOperations: number;
  totalWebSearches: number;
  cacheHitRate: number;
  effectiveMarginPct: number;
  preTokenRecords?: number;
}

interface OpBreakdown {
  operation: string;
  calls: number;
  avgCostUsd: number;
  totalCostUsd: number;
  totalWebSearches: number;
  avgTokensPerCall: number;
  cacheHitPct: number;
}

interface TenantUsage {
  tenantId: string;
  planId: string;
  tokensUsed: number;
  tokensLimit: number;
  ourCostUsd: number;
  totalWebSearches: number;
  operations: number;
}

interface ApiKeyUsage {
  apiKeyId: string;
  keyName: string;
  keyPrefix: string;
  tenantId: string;
  ourCostUsd: number;
  hypemaTokens: number;
  totalWebSearches: number;
  operations: number;
}

interface ApiUsageSummary {
  totalApiKeyOperations: number;
  totalUiOperations: number;
  apiKeyCount: number;
}

interface UnitEconomics {
  costPerResearch: number;
  costPerLeadGenerated: number;
  costPerEmailDrafted: number;
  searchesPerResearch: number;
  searchesPerLeadGen: number;
  totalLeadsCreated: number;
  totalEmailsCreated: number;
}

interface AnalyticsResponse {
  period: string;
  overview: AnalyticsOverview;
  byOperation: OpBreakdown[];
  byTenant: TenantUsage[];
  byApiKey: ApiKeyUsage[];
  apiUsageSummary: ApiUsageSummary;
  unitEconomics: UnitEconomics;
}

// ── Helpers ────────────────────────────────────────────────────

const OP_LABELS: Record<string, string> = {
  'research-company': 'Company Research',
  'generate-leads': 'Lead Generation',
  'map-csv-columns': 'CSV Mapping',
  'generate-email': 'Email Generation',
  'generate-email-preview': 'Email Preview',
};

function fmt$(n: number, decimals = 4): string {
  return `$${n.toFixed(decimals)}`;
}

/** Format a USD amount as EUR using the given rate */
function fmtEur(usd: number, rate: number, decimals = 4): string {
  return `€${(usd * rate).toFixed(decimals)}`;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-2">{children}</h2>;
}

function Card({ label, value, sub, tip }: { label: string; value: string; sub?: string; tip?: string }) {
  return (
    <div className="glass-card rounded-xl p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-base-content/40 mb-1">{label}{tip && <Tip text={tip} />}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-base-content/40 mt-0.5">{sub}</p>}
    </div>
  );
}

function Tip({ text }: { text: string }) {
  return (
    <span className="relative group inline-flex ml-1 align-middle cursor-help">
      <Info size={11} className="text-base-content/30 group-hover:text-primary/60 transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-snug text-base-content bg-base-200 border border-base-300 rounded-lg shadow-lg whitespace-normal w-52 opacity-0 group-hover:opacity-100 transition-opacity z-50">
        {text}
      </span>
    </span>
  );
}

// ── Component ──────────────────────────────────────────────────

export function PricingConfigPage() {
  const [cfg, setCfg] = useState<BillingConfig | null>(null);
  const [changelog, setChangelog] = useState<ConfigLogEntry[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [period, setPeriod] = useState<string>('30d');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Editable state (local copy)
  const [draft, setDraft] = useState<BillingConfig | null>(null);

  // Plan limits (separate from billing_config)
  const [freePlanLimit, setFreePlanLimit] = useState(500);
  const [proPlanLimit, setProPlanLimit] = useState(4900);

  // Simulation inputs
  const [simFreeUsers, setSimFreeUsers] = useState(10);
  const [simProUsers, setSimProUsers] = useState(5);
  const [simResearchPerUser, setSimResearchPerUser] = useState(10);
  const [simLeadGenPerUser, setSimLeadGenPerUser] = useState(5);
  const [simEmailsPerUser, setSimEmailsPerUser] = useState(50);
  const [simSearchesPerResearch, setSimSearchesPerResearch] = useState(3);
  const [simSearchesPerLeadGen, setSimSearchesPerLeadGen] = useState(3);

  // Operation table sort
  const [opSortKey, setOpSortKey] = useState<string>('calls');
  const [opSortAsc, setOpSortAsc] = useState(false);

  // Reconciliation
  const [recon, setRecon] = useState<any>(null);
  const [reconLoading, setReconLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      apiGet<ConfigResponse>('/admin/billing-config'),
      apiGet<AnalyticsResponse>(`/admin/analytics?period=${period}`),
      apiGet<{ id: string; limits: { tokens_per_month: number } }[]>('/billing/plans'),
    ]).then(([cfgRes, analyticsRes, plans]) => {
      setCfg(cfgRes.config);
      setDraft(structuredClone(cfgRes.config));
      setChangelog(cfgRes.changelog);
      setAnalytics(analyticsRes);
      // Load plan limits from DB
      if (Array.isArray(plans)) {
        for (const p of plans) {
          if (p.id === 'free') setFreePlanLimit(p.limits?.tokens_per_month ?? 500);
          if (p.id === 'pro') setProPlanLimit(p.limits?.tokens_per_month ?? 4900);
        }
      }
      // Prefill simulation from analytics
      if (analyticsRes?.unitEconomics) {
        const ue = analyticsRes.unitEconomics;
        if (ue.searchesPerResearch > 0) setSimSearchesPerResearch(Math.round(ue.searchesPerResearch));
        if (ue.searchesPerLeadGen > 0) setSimSearchesPerLeadGen(Math.round(ue.searchesPerLeadGen));
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    apiGet<AnalyticsResponse>(`/admin/analytics?period=${period}`).then(setAnalytics);
  }, [period]);

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const [cfgRes] = await Promise.all([
        apiPatch<ConfigResponse>('/admin/billing-config', draft),
        apiPatch<any>('/admin/plan-limits', { free: freePlanLimit, pro: proPlanLimit }),
      ]);
      setCfg(cfgRes.config);
      setDraft(structuredClone(cfgRes.config));
      setChangelog(cfgRes.changelog);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e: any) {
      setSaveMsg(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  // Live preview: compute HyPeMa tokens for a sample cost
  const previewCost = 0.12;
  const previewTokens = useMemo(() => {
    if (!draft || !draft.usd_to_eur_rate || !draft.margin_factor || !draft.tokens_per_eur) return 0;
    const costEur = previewCost * Math.max(draft.usd_to_eur_rate, 0.01);
    const customerEur = costEur * Math.max(draft.margin_factor, 0.1);
    const tokens = Math.ceil(customerEur * draft.tokens_per_eur);
    return isFinite(tokens) ? Math.max(tokens, 1) : 0;
  }, [draft]);

  // Revenue simulation
  const sim = useMemo(() => {
    if (!draft || !analytics) return null;
    const totalUsers = simFreeUsers + simProUsers;
    if (totalUsers === 0) return null;

    // Use actual avg cost per operation from analytics when available,
    // otherwise fall back to estimates from configured pricing.
    const opMap = new Map(analytics.byOperation.map((o) => [o.operation, o]));

    const researchCost = opMap.get('research-company')?.avgCostUsd
      ?? ((16000 / 1e6) * draft.models.opus.input_per_mtok + (2000 / 1e6) * draft.models.opus.output_per_mtok + simSearchesPerResearch * draft.web_search_cost);
    const leadGenCost = opMap.get('generate-leads')?.avgCostUsd
      ?? ((20000 / 1e6) * draft.models.opus.input_per_mtok + (3000 / 1e6) * draft.models.opus.output_per_mtok + simSearchesPerLeadGen * draft.web_search_cost);
    const emailCost = opMap.get('generate-email')?.avgCostUsd
      ?? ((4000 / 1e6) * draft.models.opus.input_per_mtok + (1000 / 1e6) * draft.models.opus.output_per_mtok);

    const monthlyAnthropicCost = totalUsers * (
      simResearchPerUser * researchCost
      + simLeadGenPerUser * leadGenCost
      + simEmailsPerUser * emailCost
    );

    const monthlyRevenue = simProUsers * 49; // EUR
    const monthlyAnthropicCostEur = monthlyAnthropicCost * draft.usd_to_eur_rate; // Convert USD → EUR
    const grossMargin = monthlyRevenue - monthlyAnthropicCostEur;
    const marginPct = monthlyRevenue > 0 ? (grossMargin / monthlyRevenue) * 100 : 0;
    const breakEvenPro = monthlyAnthropicCostEur > 0 ? Math.ceil(monthlyAnthropicCostEur / 49) : 0;

    // Use actual data for comparison
    const actualPeriodCost = analytics.overview.totalCostUsd;

    return {
      monthlyAnthropicCost,
      monthlyRevenue,
      grossMargin,
      marginPct,
      costPerUser: monthlyAnthropicCostEur / totalUsers,
      revenuePerUser: monthlyRevenue / totalUsers,
      breakEvenPro,
      webSearchCostPct: totalUsers > 0
        ? ((totalUsers * (simResearchPerUser * simSearchesPerResearch + simLeadGenPerUser * simSearchesPerLeadGen) * draft.web_search_cost) / monthlyAnthropicCost) * 100
        : 0,
      actualPeriodCost,
    };
  }, [draft, analytics, simFreeUsers, simProUsers, simResearchPerUser, simLeadGenPerUser, simEmailsPerUser, simSearchesPerResearch, simSearchesPerLeadGen]);

  // Sorted operations
  const sortedOps = useMemo(() => {
    if (!analytics?.byOperation) return [];
    const ops = [...analytics.byOperation];
    ops.sort((a, b) => {
      let av: number, bv: number;
      switch (opSortKey) {
        case 'operation': return opSortAsc ? a.operation.localeCompare(b.operation) : b.operation.localeCompare(a.operation);
        case 'calls': av = a.calls; bv = b.calls; break;
        case 'avgCostUsd': av = a.avgCostUsd; bv = b.avgCostUsd; break;
        case 'totalCostUsd': av = a.totalCostUsd; bv = b.totalCostUsd; break;
        case 'totalWebSearches': av = a.totalWebSearches; bv = b.totalWebSearches; break;
        case 'avgTokensPerCall': av = a.avgTokensPerCall; bv = b.avgTokensPerCall; break;
        case 'cacheHitPct': av = a.cacheHitPct; bv = b.cacheHitPct; break;
        default: av = a.calls; bv = b.calls;
      }
      return opSortAsc ? av - bv : bv - av;
    });
    return ops;
  }, [analytics?.byOperation, opSortKey, opSortAsc]);

  const toggleOpSort = useCallback((key: string) => {
    if (opSortKey === key) setOpSortAsc((p) => !p);
    else { setOpSortKey(key); setOpSortAsc(false); }
  }, [opSortKey]);

  if (loading || !draft) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  const ov = analytics?.overview;

  return (
    <div className="space-y-10 max-w-3xl mx-auto w-full animate-fade-in pb-20">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Pricing & Costs</h1>
        <p className="text-sm text-base-content/40 mt-1">Control app-wide billing parameters and view cost analytics.</p>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 1: PRICING CONFIGURATION
          ═══════════════════════════════════════════════════════════ */}

      <section className="space-y-4">
        <SectionTitle>Token Conversion</SectionTitle>
        <div className="glass-card rounded-xl p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="space-y-1">
              <span className="text-xs text-base-content/60">Margin Factor<Tip text="Markup multiplier over our internal Anthropic cost. 2.0 means the customer pays 2x what we pay. This is the primary lever for profitability." /></span>
              <input type="number" step="0.1" min="1" className="input input-bordered input-sm w-full tabular-nums"
                value={draft.margin_factor} onChange={(e) => setDraft({ ...draft, margin_factor: Math.max(0.1, parseFloat(e.target.value) || 1) })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-base-content/60">USD/EUR Rate<Tip text="How many EUR one USD buys. Example: at 1.3, a $0.10 Anthropic cost = 0.13 EUR = 26 HyPeMa tokens (at 2x margin). At 0.8, the same $0.10 = 0.08 EUR = only 16 tokens. Higher rate = USD is worth more EUR = customer pays more tokens per operation." /></span>
              <input type="number" step="0.01" min="0.01" className="input input-bordered input-sm w-full tabular-nums"
                value={draft.usd_to_eur_rate} onChange={(e) => setDraft({ ...draft, usd_to_eur_rate: parseFloat(e.target.value) || 1 })} />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-base-content/60">Tokens per EUR<Tip text="How many HyPeMa tokens equal 1 EUR of customer cost. At 100, each token is worth 0.01 EUR to the customer. Changing this rescales all token displays." /></span>
              <input type="number" step="1" min="1" className="input input-bordered input-sm w-full tabular-nums"
                value={draft.tokens_per_eur} onChange={(e) => setDraft({ ...draft, tokens_per_eur: parseInt(e.target.value) || 100 })} />
            </label>
          </div>
          <div className="px-3 py-2 rounded-lg bg-primary/5 text-xs text-base-content/60">
            <Calculator size={12} className="inline mr-1" />
            Preview: A {fmt$(previewCost, 2)} Anthropic call = <strong className="text-base-content">{previewTokens} HyPeMa tokens</strong> to the user
            <Tip text="Live calculation: $0.12 × USD/EUR rate × margin factor × tokens/EUR, rounded up. An illustrative example — actual costs vary by operation." />
          </div>
        </div>

        <SectionTitle>Plan Limits</SectionTitle>
        <div className="glass-card rounded-xl p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs text-base-content/60">Free Plan (tokens/month)<Tip text="Monthly HyPeMa token allowance for free-tier users. When exhausted, AI operations are blocked until the next billing period resets usage to 0." /></span>
              <input type="number" min="0" className="input input-bordered input-sm w-full tabular-nums"
                value={freePlanLimit} onChange={(e) => setFreePlanLimit(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-base-content/60">Pro Plan (tokens/month)<Tip text="Monthly HyPeMa token allowance for paying Pro users. Resets to 0 on each billing renewal via the Lemon Squeezy subscription_payment_success webhook." /></span>
              <input type="number" min="0" className="input input-bordered input-sm w-full tabular-nums"
                value={proPlanLimit} onChange={(e) => setProPlanLimit(parseInt(e.target.value) || 0)} />
            </label>
          </div>
        </div>

        <SectionTitle>Anthropic Model Pricing</SectionTitle>
        <div className="glass-card rounded-xl p-5 space-y-4">
          {(['opus', 'sonnet'] as const).map((model) => (
            <div key={model}>
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/50 mb-2">{model === 'opus' ? 'Claude Opus 4.6' : 'Claude Sonnet 4.6'}</p>
              <div className="grid gap-3 sm:grid-cols-5">
                <label className="space-y-1">
                  <span className="text-[10px] text-base-content/50">Input $/MTok<Tip text="Anthropic's price per million input (prompt) tokens. This is the base rate that cache multipliers are derived from. Check platform.claude.com/docs for current rates." /></span>
                  <input type="number" step="0.5" min="0" className="input input-bordered input-xs w-full tabular-nums"
                    value={draft.models[model].input_per_mtok}
                    onChange={(e) => setDraft({ ...draft, models: { ...draft.models, [model]: { ...draft.models[model], input_per_mtok: parseFloat(e.target.value) || 0 } } })} />
                </label>
                <label className="space-y-1">
                  <span className="text-[10px] text-base-content/50">Output $/MTok<Tip text="Anthropic's price per million output (completion) tokens. Includes thinking tokens when adaptive thinking is enabled." /></span>
                  <input type="number" step="0.5" min="0" className="input input-bordered input-xs w-full tabular-nums"
                    value={draft.models[model].output_per_mtok}
                    onChange={(e) => setDraft({ ...draft, models: { ...draft.models, [model]: { ...draft.models[model], output_per_mtok: parseFloat(e.target.value) || 0 } } })} />
                </label>
                <label className="space-y-1">
                  <span className="text-[10px] text-base-content/50">Cache 5m (x)<Tip text="Multiplier of the input rate for 5-minute ephemeral cache writes. Anthropic default: 1.25x. These tokens are billed when prompt caching creates short-lived cache entries." /></span>
                  <input type="number" step="0.05" min="0" className="input input-bordered input-xs w-full tabular-nums"
                    value={draft.models[model].cache_5m_multiplier}
                    onChange={(e) => setDraft({ ...draft, models: { ...draft.models, [model]: { ...draft.models[model], cache_5m_multiplier: parseFloat(e.target.value) || 0 } } })} />
                </label>
                <label className="space-y-1">
                  <span className="text-[10px] text-base-content/50">Cache 1h (x)<Tip text="Multiplier of the input rate for 1-hour ephemeral cache writes. Anthropic default: 2.0x. Higher cost but cache persists longer, saving on subsequent calls." /></span>
                  <input type="number" step="0.1" min="0" className="input input-bordered input-xs w-full tabular-nums"
                    value={draft.models[model].cache_1h_multiplier}
                    onChange={(e) => setDraft({ ...draft, models: { ...draft.models, [model]: { ...draft.models[model], cache_1h_multiplier: parseFloat(e.target.value) || 0 } } })} />
                </label>
                <label className="space-y-1">
                  <span className="text-[10px] text-base-content/50">Cache Read (x)<Tip text="Multiplier of the input rate for cache hits (reads). Anthropic default: 0.1x. Cache reads are 90% cheaper than uncached input, making caching highly cost-effective." /></span>
                  <input type="number" step="0.01" min="0" className="input input-bordered input-xs w-full tabular-nums"
                    value={draft.models[model].cache_read_multiplier}
                    onChange={(e) => setDraft({ ...draft, models: { ...draft.models, [model]: { ...draft.models[model], cache_read_multiplier: parseFloat(e.target.value) || 0 } } })} />
                </label>
              </div>
            </div>
          ))}
          <label className="space-y-1">
            <span className="text-xs text-base-content/60">Web Search Cost (per request)<Tip text="Anthropic charges per web search invocation ($10/1,000 = $0.01 each). Research and lead generation use multiple searches per call. This is often the largest cost component." /></span>
            <input type="number" step="0.001" min="0" className="input input-bordered input-sm w-40 tabular-nums"
              value={draft.web_search_cost} onChange={(e) => setDraft({ ...draft, web_search_cost: parseFloat(e.target.value) || 0 })} />
          </label>
        </div>

        {/* Save + Changelog */}
        <div className="flex items-center gap-3">
          <button className="btn btn-primary btn-sm gap-2" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Configuration
          </button>
          {saveMsg && <span className={`text-xs ${saveMsg.startsWith('Error') ? 'text-error' : 'text-success'}`}>{saveMsg}</span>}
        </div>

      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 2: COST ANALYTICS
          ═══════════════════════════════════════════════════════════ */}

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <SectionTitle>Cost Analytics<Tip text="All data comes from HyPeMa's internal api_usage table in Supabase. Not from Anthropic's billing API. Use the Anthropic Usage & Cost Admin API separately for reconciliation." /></SectionTitle>
          <div className="flex gap-1">
            {['24h', '7d', '30d', 'all'].map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${period === p ? 'bg-primary text-primary-content' : 'text-base-content/40 hover:text-base-content/60'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>

        {ov && (ov.preTokenRecords ?? 0) > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20 text-xs text-warning">
            <AlertTriangle size={13} className="shrink-0" />
            <span>
              <strong>{ov.preTokenRecords} pre-migration records excluded.</strong>{' '}
              Usage logged before the HyPeMa token system was deployed has no token attribution and is excluded from all metrics below.
            </span>
          </div>
        )}

        {ov && (
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Card label="Our Cost" value={fmtEur(ov.totalCostUsd, draft.usd_to_eur_rate, 2)} tip="Total cost in EUR we paid Anthropic in this period (post-migration records only). Converted from USD using the configured USD/EUR rate." />
            <Card label="HyPeMa Tokens" value={(ov.totalHypemaTokens ?? 0).toLocaleString()} tip="Total HyPeMa tokens consumed by all users in this period." />
            <Card label="Margin" value={ov.effectiveMarginPct != null && isFinite(ov.effectiveMarginPct) && ov.totalHypemaTokens > 0 ? `${ov.effectiveMarginPct.toFixed(1)}%` : 'N/A'} tip="Effective margin: (customer EUR value - our EUR cost) / customer EUR value. Shows N/A when no tokens recorded yet." />
            <Card label="Operations" value={ov.totalOperations.toLocaleString()} tip="Total AI API calls in this period." />
            <Card label="Web Searches" value={ov.totalWebSearches.toLocaleString()} tip="Total Anthropic web searches. Each costs the configured web search rate." />
            <Card label="Cache Hit Rate" value={`${(ov.cacheHitRate ?? 0).toFixed(1)}%`} tip="Percentage of input tokens served from cache vs total input." />
          </div>
        )}

        {/* Per-operation table (sortable) */}
        {sortedOps.length > 0 && (
          <div className="glass-card rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-base-300 text-left text-[10px] text-base-content/40 uppercase tracking-wider">
                  {([
                    ['operation', 'Operation', false],
                    ['calls', 'Calls', true],
                    ['avgCostUsd', 'Avg Cost €', true],
                    ['totalCostUsd', 'Total Cost €', true],
                    ['totalWebSearches', 'Searches', true],
                    ['avgTokensPerCall', 'Avg Tok/Call', true],
                    ['cacheHitPct', 'Cache %', true],
                  ] as [string, string, boolean][]).map(([key, label, rightAlign]) => (
                    <th
                      key={key}
                      className={`px-3 py-2 cursor-pointer select-none hover:text-base-content/60 transition-colors ${rightAlign ? 'text-right' : ''} ${key === 'avgTokensPerCall' || key === 'cacheHitPct' ? 'hidden sm:table-cell' : ''}`}
                      onClick={() => toggleOpSort(key)}
                    >
                      {label}
                      <span className={`ml-0.5 ${opSortKey === key ? 'text-primary' : 'text-base-content/20'}`}>
                        {opSortKey === key ? (opSortAsc ? '\u25B2' : '\u25BC') : '\u25BC'}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300/50">
                {sortedOps.map((op) => (
                  <tr key={op.operation}>
                    <td className="px-3 py-2">{OP_LABELS[op.operation] ?? op.operation}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{op.calls}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEur(op.avgCostUsd, draft.usd_to_eur_rate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtEur(op.totalCostUsd, draft.usd_to_eur_rate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{op.totalWebSearches}</td>
                    <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell">{op.avgTokensPerCall.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell">{op.cacheHitPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-tenant table */}
        {analytics && analytics.byTenant.length > 0 && (
          <>
            <SectionTitle>Per-Tenant Usage</SectionTitle>
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-base-300 text-left text-[10px] text-base-content/40 uppercase tracking-wider">
                    <th className="px-3 py-2">Tenant</th>
                    <th className="px-3 py-2">Plan</th>
                    <th className="px-3 py-2 text-right">Tokens</th>
                    <th className="px-3 py-2 text-right">Our Cost €</th>
                    <th className="px-3 py-2 text-right">Searches</th>
                    <th className="px-3 py-2 text-right">Ops</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-base-300/50">
                  {analytics.byTenant.map((t) => (
                    <tr key={t.tenantId}>
                      <td className="px-3 py-2 font-mono text-[10px]">{t.tenantId.slice(0, 8)}...</td>
                      <td className="px-3 py-2 capitalize">{t.planId}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.tokensUsed} / {t.tokensLimit === -1 ? '∞' : t.tokensLimit}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEur(t.ourCostUsd, draft.usd_to_eur_rate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.totalWebSearches}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.operations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* API vs UI usage split */}
        {analytics?.apiUsageSummary && (analytics.apiUsageSummary.totalApiKeyOperations > 0 || analytics.apiUsageSummary.totalUiOperations > 0) && (
          <>
            <SectionTitle>API vs Web App Usage</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-3">
              <Card label="Web App" value={analytics.apiUsageSummary.totalUiOperations.toLocaleString()} sub="operations via UI" tip="AI operations triggered from the web app (JWT auth). These have api_key_id = NULL in the database." />
              <Card label="API" value={analytics.apiUsageSummary.totalApiKeyOperations.toLocaleString()} sub="operations via API keys" tip="AI operations triggered via the public REST API (API key auth). Tracked with api_key_id for per-key attribution." />
              <Card label="Active API Keys" value={String(analytics.apiUsageSummary.apiKeyCount)} sub="keys used in period" tip="Number of distinct API keys that made at least one AI operation in this period." />
            </div>
          </>
        )}

        {/* Per-API-key table */}
        {analytics && analytics.byApiKey && analytics.byApiKey.length > 0 && (
          <>
            <SectionTitle>Per-API-Key Usage</SectionTitle>
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-base-300 text-left text-[10px] text-base-content/40 uppercase tracking-wider">
                    <th className="px-3 py-2">Key</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 text-right">Tokens</th>
                    <th className="px-3 py-2 text-right">Our Cost €</th>
                    <th className="px-3 py-2 text-right">Searches</th>
                    <th className="px-3 py-2 text-right">Ops</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-base-300/50">
                  {analytics.byApiKey.map((k) => (
                    <tr key={k.apiKeyId}>
                      <td className="px-3 py-2 font-mono text-[10px]">{k.keyPrefix}...</td>
                      <td className="px-3 py-2">{k.keyName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{k.hypemaTokens.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEur(k.ourCostUsd, draft.usd_to_eur_rate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{k.totalWebSearches}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{k.operations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Unit economics */}
        {analytics?.unitEconomics && (
          <>
            <SectionTitle>Unit Economics</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <Card label="Cost / Lead" value={fmtEur(analytics.unitEconomics.costPerLeadGenerated, draft.usd_to_eur_rate)} sub={`${analytics.unitEconomics.totalLeadsCreated} leads total`} tip="Our cost per lead in EUR. Calculated as total lead-gen cost / total leads generated in the period." />
              <Card label="Cost / Email" value={fmtEur(analytics.unitEconomics.costPerEmailDrafted, draft.usd_to_eur_rate)} sub={`${analytics.unitEconomics.totalEmailsCreated} emails total`} tip="Our cost per email in EUR. Calculated as total email-gen cost / total emails generated in the period." />
              <Card label="Searches / Lead Gen" value={String(analytics.unitEconomics.searchesPerLeadGen)} tip="Average web searches per lead generation call. High values indicate Claude is doing thorough research. Each search costs the configured web search rate." />
              <Card label="Cost / Research" value={fmtEur(analytics.unitEconomics.costPerResearch, draft.usd_to_eur_rate)} sub={`${analytics.unitEconomics.searchesPerResearch} searches avg`} tip="Our average cost per company research call in EUR. Includes input/output tokens + web search costs." />
            </div>
          </>
        )}
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3: REVENUE SIMULATION
          ═══════════════════════════════════════════════════════════ */}

      <section className="space-y-4">
        <SectionTitle>Revenue Simulation<Tip text="Pure frontend calculation — no backend calls. Uses actual avg cost per operation from analytics data. Adjust inputs to model different growth scenarios." /></SectionTitle>
        <div className="glass-card rounded-xl p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Free Users<Tip text="Projected number of free-tier users. They consume Anthropic API costs but generate no direct revenue. Key driver of infrastructure cost." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simFreeUsers} onChange={(e) => setSimFreeUsers(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Pro Users<Tip text="Projected number of paying Pro subscribers at EUR 49/month. Only source of revenue. Break-even = total Anthropic cost / 49." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simProUsers} onChange={(e) => setSimProUsers(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Research / User / Mo<Tip text="Average company research calls per user per month. Each research call uses web search (high cost) and Opus for deep analysis." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simResearchPerUser} onChange={(e) => setSimResearchPerUser(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Lead Gen / User / Mo<Tip text="Average lead generation calls per user per month. Lead gen is the most expensive operation due to heavy web search usage (often 30-50 searches per call)." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simLeadGenPerUser} onChange={(e) => setSimLeadGenPerUser(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Emails / User / Mo<Tip text="Average email generation calls per user per month. Emails are the cheapest operation (no web search, smaller prompts). Typically the highest volume operation." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simEmailsPerUser} onChange={(e) => setSimEmailsPerUser(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Searches / Research<Tip text="Average web searches per company research call. Auto-filled from actual analytics data. Each search costs $0.01 from Anthropic." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simSearchesPerResearch} onChange={(e) => setSimSearchesPerResearch(parseInt(e.target.value) || 0)} />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] text-base-content/50">Searches / Lead Gen<Tip text="Average web searches per lead generation call. Auto-filled from actual analytics. This is typically the biggest cost driver due to high search volume per call." /></span>
              <input type="number" min="0" className="input input-bordered input-xs w-full tabular-nums"
                value={simSearchesPerLeadGen} onChange={(e) => setSimSearchesPerLeadGen(parseInt(e.target.value) || 0)} />
            </label>
          </div>

          {sim && (
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 pt-2 border-t border-base-300">
              <Card label="Monthly Anthropic Cost" value={`€${(sim.monthlyAnthropicCost * draft.usd_to_eur_rate).toFixed(2)}`} tip="Projected monthly cost we pay Anthropic (in EUR). Uses actual avg cost per operation from analytics data." />
              <Card label="Monthly Revenue" value={`€${sim.monthlyRevenue.toFixed(0)}`} tip="Projected monthly revenue from Pro subscriptions only. Free users generate no direct revenue. Formula: Pro users x EUR 49." />
              <Card label="Gross Margin" value={`€${sim.grossMargin.toFixed(0)} (${sim.marginPct.toFixed(0)}%)`} tip="Revenue minus Anthropic cost. Negative means we lose money. Does not include infrastructure, salaries, or other overhead." />
              <Card label="Break-even Pro Users" value={String(sim.breakEvenPro)} tip="Minimum Pro subscribers needed to cover the projected Anthropic API cost. Formula: total Anthropic cost / EUR 49 (rounded up)." />
              <Card label="Cost / User" value={`€${sim.costPerUser.toFixed(2)}`} tip="Projected Anthropic cost per user per month in EUR, averaged across all users (free + pro). Assumes all users have equal usage patterns." />
              <Card label="Revenue / User" value={`€${sim.revenuePerUser.toFixed(2)}`} tip="Revenue per user averaged across all users (free + pro). Free users dilute this metric since they pay nothing." />
              <Card label="Web Search Cost %" value={`${sim.webSearchCostPct.toFixed(1)}%`} sub="of total Anthropic cost" tip="What percentage of total Anthropic spend goes to web searches. High values suggest lead gen / research dominate costs." />
              <Card label={`Actual Cost (${period})`} value={fmtEur(sim.actualPeriodCost, draft.usd_to_eur_rate, 2)} sub="for comparison" tip={`Real Anthropic cost (EUR) from the selected ${period} period. Compare against the projected cost to validate your simulation assumptions.`} />
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CHANGELOG (bottom of page)
          ═══════════════════════════════════════════════════════════ */}

      {changelog.length > 0 && (
        <section className="space-y-2">
          <SectionTitle>Configuration Changelog</SectionTitle>
          <div className="glass-card rounded-xl p-4 max-h-64 overflow-y-auto space-y-1.5">
            {changelog.map((entry) => (
              <div key={entry.id} className="text-[11px] text-base-content/50 flex gap-2">
                <Clock size={10} className="shrink-0 mt-0.5" />
                <span className="tabular-nums text-base-content/40">
                  {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })},{' '}
                  {new Date(entry.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span>
                  {Object.entries(entry.changes).map(([key, { old: o, new: n }]) => (
                    <span key={key} className="mr-2">{key}: <span className="text-error/60">{String(o)}</span> <span className="text-base-content/30">&rarr;</span> <span className="text-success/80">{String(n)}</span></span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════
          ANTHROPIC COST RECONCILIATION
          ═══════════════════════════════════════════════════════════ */}

      <section className="space-y-4">
        <SectionTitle>Anthropic Cost Reconciliation<Tip text="Compares our internal cost tracking (api_usage table) against Anthropic's official billing via their Admin API. Requires an ANTHROPIC_ADMIN_API_KEY env var (sk-ant-admin...). Helps detect missed usage from silent failures, pause_turn gaps, or pricing mismatches." /></SectionTitle>
        <div className="glass-card rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-base-content/50">
              Compare internal records against Anthropic's Usage & Cost API for the last 30 days.
            </p>
            <button
              className="btn btn-sm btn-outline gap-2"
              onClick={async () => {
                setReconLoading(true);
                try {
                  const data = await apiGet<any>('/admin/reconciliation');
                  setRecon(data);
                } catch (e: any) {
                  setRecon({ available: false, reason: e.message });
                } finally {
                  setReconLoading(false);
                }
              }}
              disabled={reconLoading}
            >
              {reconLoading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              Run Reconciliation
            </button>
          </div>

          {recon?.apiKeyNote && (
            <p className="text-[10px] text-base-content/40">{recon.apiKeyNote}</p>
          )}

          {recon && !recon.available && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 text-xs text-warning">
              <AlertTriangle size={14} className="shrink-0" />
              <span>{recon.reason || 'Anthropic Admin API not available'}</span>
            </div>
          )}

          {recon?.available && (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                <Card label="Internal Cost" value={fmt$(recon.internal.totalUsd, 2)} sub={`${recon.internal.operations} operations`} tip="Total cost calculated by HyPeMa from the api_usage table using our configured pricing." />
                <Card
                  label="Anthropic Reported"
                  value={recon.anthropic ? fmt$(recon.anthropic.totalUsd, 2) : 'Error'}
                  sub={recon.anthropicError ? 'API error' : undefined}
                  tip="Total cost reported by Anthropic's official Cost API. This is what they actually bill us."
                />
                <Card
                  label="Delta"
                  value={recon.anthropic ? fmt$(Math.abs(recon.delta), 2) : 'N/A'}
                  sub={recon.delta > 0 ? 'Anthropic charged more' : recon.delta < 0 ? 'We over-tracked' : 'Exact match'}
                  tip="Difference between Anthropic's reported cost and our internal tracking. Positive = we missed some costs (under-tracked). Negative = we over-estimated."
                />
                <div className="glass-card rounded-xl p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-base-content/40 mb-1">
                    Status<Tip text="Green if delta is within 5%. Yellow if 5-15%. Red if over 15%. Large deltas suggest pause_turn token loss, silent logUsage failures, or pricing constant mismatches." />
                  </p>
                  {recon.anthropic ? (
                    <div className="flex items-center gap-2">
                      {Math.abs(recon.deltaPct) <= 5 ? (
                        <CheckCircle2 size={20} className="text-success" />
                      ) : Math.abs(recon.deltaPct) <= 15 ? (
                        <AlertTriangle size={20} className="text-warning" />
                      ) : (
                        <AlertTriangle size={20} className="text-error" />
                      )}
                      <span className={`text-xl font-bold tabular-nums ${Math.abs(recon.deltaPct) <= 5 ? 'text-success' : Math.abs(recon.deltaPct) <= 15 ? 'text-warning' : 'text-error'}`}>
                        {recon.deltaPct > 0 ? '+' : ''}{recon.deltaPct.toFixed(1)}%
                      </span>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-base-content/30">N/A</p>
                  )}
                </div>
              </div>

              {recon.anthropicError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-error/10 text-xs text-error">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span>{recon.anthropicError}</span>
                </div>
              )}

              {recon.dailyComparison?.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-base-300">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-base-300 text-left text-[10px] text-base-content/40 uppercase tracking-wider bg-base-200/30">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2 text-right">Internal</th>
                        <th className="px-3 py-2 text-right">Anthropic</th>
                        <th className="px-3 py-2 text-right">Delta</th>
                        <th className="px-3 py-2 text-right">Delta %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-base-300/50">
                      {recon.dailyComparison.map((d: any) => {
                        const dayDelta = d.anthropic - d.internal;
                        const dayDeltaPct = d.internal > 0 ? ((d.anthropic - d.internal) / d.internal) * 100 : (d.anthropic > 0 ? 100 : 0);
                        return (
                          <tr key={d.date}>
                            <td className="px-3 py-1.5 tabular-nums">{d.date}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(d.internal)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(d.anthropic)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${Math.abs(dayDelta) < 0.001 ? 'text-base-content/30' : dayDelta > 0 ? 'text-error/70' : 'text-success/70'}`}>
                              {dayDelta > 0 ? '+' : ''}{fmt$(dayDelta)}
                            </td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${Math.abs(dayDeltaPct) < 5 ? 'text-base-content/30' : dayDeltaPct > 0 ? 'text-error/70' : 'text-success/70'}`}>
                              {d.internal === 0 && d.anthropic === 0 ? '—' : `${dayDeltaPct > 0 ? '+' : ''}${dayDeltaPct.toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!recon && !reconLoading && (
            <p className="text-xs text-base-content/30">Click "Run Reconciliation" to compare internal tracking against Anthropic's billing data.</p>
          )}
        </div>
      </section>
    </div>
  );
}
